import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Heart, Star } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { inr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

export default function ProductCard({ product, index = 0 }) {
  const { user, toggleWishlist } = useAuth();
  const wished = user && (user.wishlist || []).includes(product.id);
  const price = product.sale_price || product.price;
  const off = product.sale_price
    ? Math.round((1 - product.sale_price / product.price) * 100)
    : 0;
  const out = product.total_stock === 0;

  const images = (product.images || []).filter(Boolean);
  const [activeImage, setActiveImage] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (!isHovered || images.length < 2) return;

    intervalRef.current = setInterval(() => {
      setActiveImage((current) => (current + 1) % images.length);
    }, 1200);

    return () => {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    };
  }, [isHovered, images.length]);

  const handleMouseEnter = () => {
    if (images.length > 1) {
      setActiveImage(1);
      setIsHovered(true);
    }
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
    setActiveImage(0);
  };

  const wish = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!user) {
      return toast.error("Login to save items to your wishlist");
    }

    const added = await toggleWishlist(product.id);
    toast.success(added ? "Added to wishlist" : "Removed from wishlist");
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.5, delay: (index % 4) * 0.08 }}
      data-testid={`product-card-${product.slug}`}
    >
      <Link to={`/product/${product.slug}`} className="group block">
        <div
          className="relative overflow-hidden bg-ink-surface aspect-[3/4] border border-[#262626] group-hover:border-gold/50 transition-colors duration-300"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <AnimatePresence initial={false}>
            {images.length > 0 && (
              <motion.img
                key={images[activeImage] || images[0]}
                src={images[activeImage] || images[0]}
                alt={product.name}
                loading="lazy"
                initial={{ x: "100%" }}
                animate={{ x: 0 }}
                exit={{ x: "-100%" }}
                transition={{ duration: 0.4, ease: "easeInOut" }}
                className={`absolute inset-0 h-full w-full object-cover ${
                  out ? "grayscale opacity-60" : ""
                }`}
              />
            )}
          </AnimatePresence>

          {off > 0 && (
            <span className="absolute top-3 left-3 z-10 bg-gold text-ink text-[10px] font-bold px-2 py-1 uppercase tracking-wider">
              {off}% off
            </span>
          )}

          {product.new_arrival && !off && (
            <span className="absolute top-3 left-3 z-10 bg-ink border border-gold text-gold text-[10px] font-bold px-2 py-1 uppercase tracking-wider">
              New
            </span>
          )}

          {out && (
            <span className="absolute inset-x-0 bottom-0 z-10 bg-ink/90 text-center text-xs uppercase tracking-widest py-2 text-muted-foreground">
              Out of stock
            </span>
          )}

          <button
            onClick={wish}
            data-testid={`wishlist-btn-${product.slug}`}
            className={`absolute top-3 right-3 z-20 p-2 bg-ink/70 backdrop-blur border border-[#262626] transition-colors hover:border-gold ${
              wished ? "text-gold" : "text-muted-foreground"
            }`}
          >
            <Heart size={16} fill={wished ? "#D4AF37" : "none"} />
          </button>
        </div>

        <div className="pt-3">
          <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            {product.subcategory || product.category}
          </p>

          <h3 className="font-display font-bold text-sm mt-0.5 group-hover:text-gold transition-colors">
            {product.name}
          </h3>

          <div className="flex items-center gap-2 mt-1">
            <span className="text-gold font-bold text-sm">{inr(price)}</span>

            {product.sale_price && (
              <span className="text-muted-foreground text-xs line-through">
                {inr(product.price)}
              </span>
            )}

            {product.rating > 0 && (
              <span className="ml-auto flex items-center gap-1 text-xs text-muted-foreground">
                <Star size={11} fill="#D4AF37" className="text-gold" />
                {product.rating}
              </span>
            )}
          </div>
        </div>
      </Link>
    </motion.div>
  );
}
